import crypto from 'crypto';
import dayjs from 'dayjs';

/**
 * This is useful for abortting events or when you just want to ensure
 * a value is a function (that does nothing).
 * 
 * @param {EventObect} event The event that was triggered
 * @returns false
 */
export function fn (event) {
  event.stopPropagation && event.stopPropagation();
  event.preventDefault && event.preventDefault();
  return false;
}

/**
 * Use it when you want an event to be disabled or ignored.
 * 
 * @param {Event} event The event to be aborted
 * @returns false
 */
export function abortEvent (event) {
  event.stopPropagation && event.stopPropagation();
  event.preventDefault && event.preventDefault();
  return false;
}

/**
 * Checks if a value is in a valid UUID format
 * 
 * @param {String} value The value to be checked
 * @returns Boolean
 */
export function isUUID (value = "") {
  return !!value.toString().match(
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/ // eslint-disable-line
  );
}

/**
 * Checks if a value is in a valid UUID format.
 * This is an alias to isUUID.
 * 
 * @param {String} value The value to be checked
 * @returns Boolean
 */
export function isHash (value = "") {
  return isUUID(value);
}

/**
 * Checks whether the value is a valid url or not
 * 
 * @param {String} value The value to be checked
 * @returns Boolean
 */
export function isURL (value = "") {
  try {
    new URL(value);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Checkes if a given string is a valid path
 * 
 * @param {String} value The value to be checked
 * @returns Boolean
 */
export function isPath (value = "") {
  if (value == '/') {
    return true;
  }

  return !!value.toString().replace(/\\/g, '/').match(
    /^(\/?([a-zA-Z0-9-_\.,])+)+$/ // eslint-disable-line
  );
}

export function urlFromStorage (value) {
  value = value.toString();

  if (isURL(value)) {
    return value;
  }

  const pathToAPI = "/api/storage-drive/fetch?id=";

  if (isUUID(value)) {
    return pathToAPI + value;
  }

  if (typeof value === 'object') {
    const url = value.url || value.public_url;

    if (url) {
      return url;
    }

    if (value.id) {
      return pathToAPI + value.id;
    }
  }

  return '/images/image-not-found.webp';
}

/**
 * If an item is an array, returns it. Otherwise, return an array with it.
 * This ensures a value will always be an array.
 * 
 * @param {Any} item The item to be turned into an array
 * @returns Array
 */
export function toArray(item) {
  if (!item) {
    return [];
  }

  if (Array.isArray(item)) {
    return item;
  }
  return [item];
}

/**
 * This is useful for ensuring something should wait for a given time before procedding.
 * 
 * Eg.:
 *   await sleep(1000); // will wait for 1 second
 * 
 * @param {Number} ms Mileseconds
 * @returns Promise
 */
export async function sleep(ms) {
  return await (new Promise(resolve => setTimeout(resolve, ms)));
}

/**
 * Will remove accents (diacritics) from a given string, replacing them to the actual,
 * correct characters.
 * 
 * Eg.:
 *   removeDiacritics("Um textão contendo acentuação.");
 *  returns "Um textao contendo acentuacao."
 * @param {String} word The text to be treated
 * @param {String} wordSeparator An optional sepparator to be used (defaults to " ")
 * @returns String
 */
export function removeDiacritics(word, wordSeparator = ' ') {
  return ('' + word)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ /g, wordSeparator);
}

/**
 * Will apply a cammelCase format for the given string.
 * Eg.:
 *   camelize("some-random text");
 *   returns: "someRandomText"
 * Eg.:
 *   camelize("some-random text", true);
 *   returns: "SomeRandomText"
 * 
 * @param {String} str The text to be treated
 * @param {Boolean} upper Trus if the first letter should also be upper cased
 * @returns String
 */
export function camelize(str, upper = false) {
  // return str.replace(/(?:^\w|[A-Z\u0300-\u036f]|\b\w)/g, function (word, index) { // eslint-disable-line
  return str.replace(/(?:^\w|\b\w)/g, function (word, index) { // eslint-disable-line
    return (index === 0 && !upper) ? word.toLowerCase() : word.toUpperCase();
  }).replace(/[\s_-]+/g, '');
}

/**
 * Will take a string and try to make it look like a word (or a set of words).
 * Eg.:
 *   wordfy("some-randomTextWith_different tempére");
 *   returns: "some random Text With different tempére"
 * 
 * @param {String} word The text to be treated
 * @returns String
 */
export function wordfy(word) {
  return word
    .replace(/[-_ @/><?&%!]/g, " ")
    .replace(/([^^])([A-Z][0-9a-z])/g, '$1 $2');
}

/**
 * Will transform a text into a snake_case format.
 * Eg.:
 *   snakefy("some random-text-withDifferentFormats");
 *   returns: "some_random_text_with_different_formats"
 * 
 * @param {String} word The text to be transformed
 * @returns String
 */
export function snakefy(word, char = '_') {
  word = wordfy(word);
  return word.replace(/ /g, char).toLowerCase();
}

/**
 * Transforms a given text into a slugified text.
 * 
 * Eg.:
 *  slugfy("Um texto com acentuação e LETRAS MAIÚSCULAS");
 *  returns "um-texto-com-acentuacao-e-letras-maiusculas"
 * 
 * @param {String} val The text to be treated
 * @returns String
 */
export function slugfy(val, options = {}) {
  let slug = removeDiacritics(val, '-')
    .replace(/[^a-z0-9-]/ig, '')
    // .replace(/-$/, '')
    .replace(/--/g, '-')
    .toLowerCase();

  if (options.startWithSlash) {
    slug = '/' + slug;
  }
  if (options.trailingSlash) {
    slug += '/';
  }
  return slug.replace(/\/\//g, '/');
}

/**
 * @alias slugfy
 */
export const canonize = slugfy;

/**
 * Will format a value in a localized money/currency.
 * 
 * Eg.:
 *   currency(123456789)
 *   returns: "R$123.456.789,00"
 * Eg.:
 *   currency(123456789, 'en-US')
 *   returns: "R$123,456,789.00"
 * Eg.:
 *   currency(123456789, 'en-US', "USD")
 *   returns: "$123,456,789.00"
 * Eg.:
 *   currency(123456789, 'es-AR', "ARS")
 *   returns: "$123.456.789,00"
 * 
 * @param {Number} value The value
 * @param {String} lang The language into which the currency shall be localized (default is "pt-BR")
 * @param {String} currency The currency international id in 3 letters (default is "BRL")
 * @returns String
 */
export function currency(value, lang = 'en-US', currency = 'USD') {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency,
  }).format(value);
}

let isIOS = -1;
/**
 * Will check wether the current Operating System is iOS or not
 * 
 * @returns Boolean
 */
export function iOS() {
  // isIOS will cache it
  if (isIOS === -1) {
    isIOS = [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  }
  return isIOS;
}

/**
 * Calculates the aspect ratio for a given asset based on its
 * original size and max available area.
 * 
 * @param {Number} srcWidth The original width
 * @param {Number} srcHeight The original height
 * @param {Number} maxWidth The limit width
 * @param {Number} maxHeight The limit for the height
 * @returns Object {width, height}
 */
export function calculateAspectRatioFit(
  srcWidth,
  srcHeight,
  maxWidth,
  maxHeight
) {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return { width: srcWidth * ratio, height: srcHeight * ratio };
}

/**
 * Generates the current (and formated) date and time.
 *
 * @returns {object} currentDate: string no formato "DD/MM/YYYY"
 *                   currentTime: string no formato "HH:MM:SS GMT-0300 (Brasilia Standard Time)"
 */
export function getDateAndTime(dateTime) {
  dateTime = dateTime || new Date();
  const curDate = Intl.DateTimeFormat("pt-BR").format(dateTime);
  const curTime = dateTime.toTimeString();
  return {
    currentDate: curDate,
    currentTime: curTime
  };
}

/**
 * Converts, if needed, date from ISO to BR
 *
 * @param {string} inputDate Original date in format YYYY-MM-DD
 * @returns String A string in the format DD/MM/YYYY
 */
export function dateIso2Br(inputDate) {
  let converted = String(inputDate).trim();
  if (converted.length > 0 && converted.indexOf('-') >= 0) {
    converted = converted.match(/\d+/g);
    const [year, month, day] = converted;
    converted = `${day}/${month}/${year}`;
  }
  return converted;
}

/**
 * Will receive a date object or raw string and parse it, returning in
 * the giving format.
 * 
 * It uses dayjs as library for formating.
 * 
 * @param {Date} date The date object or ISO string
 * @param {String} format The format to be returned (a string like `d/m/y`)
 * @returns 
 */
export function dateFormater (date, format = '') {
  const loc = typeof navigator !== 'undefined' ? navigator?.language : 'en-US';
  format = format.replace(/y/g, 'Y'); // dayjs requires y to be uppercase
  return dayjs(date).locale(loc).format(format);
}

export function timeFormater(time) {
  let date = new Date(time);
  let options = {
    weekday: undefined, year: undefined, month: undefined,
    day: undefined, hour: "2-digit", minute: "2-digit"
  };

  return date.toLocaleTimeString(
    (navigator && navigator?.language)
      ? navigator.language
      : "en-us"
    ,
    options
  );
}

export function dateFormat (time) {
  let date = new Date(time);
  let options = {
    weekday: undefined, year: undefined, month: undefined,
    day: undefined, hour: "2-digit", minute: "2-digit"
  };

  return date.toLocaleTimeString(
    (navigator && navigator?.language)
      ? navigator.language
      : "en-us"
    ,
    options
  );
}

/**
 * Will remove part of the string in the middle of it, if  it is bigger
 * than the given limit.
 * This will replace the removed portion of the string with ellipsis
 * 
 * For example:
 * shortenString("some really big string here")
 * Will output "some reall…ring here"
 * 
 * @param {String} string The string to be shortned
 * @param {Number} size The max size for the final string
 * @param {String} symbol The symbol to be placed in the removed part of the string
 * @returns String
 */
export function shortenString(string = '', size = 20, symbol = "…") {
  if (!string) {
    return '';
  }

  const len = string.length;
  if (len <= size) {
    return string;
  }

  const diff = len - size;
  const firstPart = string.substring(0, len / 2 - diff / 2);
  const lastPart = string.substring(len / 2 + diff / 2 + 1, len);
  return firstPart + symbol + lastPart;
}

export function hideMiddleOfString (
  content = '',
  first = 4,
  last = 4,
  symbol = "*"
) {
  // content = '' + content;
  if (!content || content.length <= first + last) {
    // too short to hide anything
    return content;
  }

  return content.substr(0, first)
    + ((new Array(content.length - first - last)).fill(symbol).join(''))
    + content.substr(-1 * last);
}

export function mapOr(arr = [], fn = _ => { }, empty = '') {
  if (!arr || arr.length === 0 || !Array.isArray(arr)) {
    return empty;
  }

  return arr.map(fn);
}

/**
 * Converte, se necessário, data de BR para ISO
 *
 * @param {string} inputDate Data de entrada no formato DD/MM/YYYY
 * @returns Data de saída no formato YYYY-MM-DD
 */
export function dateBr2Iso(inputDate) {
  let converted = String(inputDate).trim();
  if (converted.length > 0 && converted.indexOf('/') >= 0) {
    converted = converted.match(/\d+/g);
    const [day, month, year] = converted;
    converted = `${year}-${month}-${day}`;
  }
  return converted;
}

/**
 * Cópia em profundidade de um objeto em outro, ignorando campos
 * indefinidos do segundo.
 *
 * @param {object} obj1 Primeiro objeto, que deve receber os valores
 * @param {object} obj2 Segundo objeto, que deve fornecer valores novos
 * para uma ou mais propriedades
 *
 * @returns O primeiro objeto atualizado
 */
export function deepCopy(obj1, obj2) {
  for (const field in obj1) {
    const field1Type = typeof obj1[field];
    const field2Type = typeof obj2[field];
    const sameType = field1Type === field2Type;

    if (field2Type === 'undefined') {
      continue;
    }

    // Se ambos são mesmo tipos e objeto, entra na recursão
    if (sameType && field2Type === 'undefined') {
      return deepCopy(obj1[field], obj2[field]);
    }

    obj1[field] = obj2[field];
  }

  return obj1;
}

export function deepClone (obj) {
  return structuredClone(obj);
}

/**
 * Opens a popup in the center of the screen.
 * 
 * @param {String} url The url of the popup
 * @param {String} title The title of the popup
 * @param {Number} w The width the popup should have
 * @param {Number} h The popup's height
 * @param {Boolean} focus If it should focus the popup as soon as it opens or not
 * @returns Window reference
 */
export function popupCenterScreen (url, title, w, h, focus) {
  w = w || screen.availWidth;
  h = h || screen.availHeight;
  const top = (screen.height - h) / 4,
    left = (screen.width - w) / 2;
  const popup = window.open(
    url,
    title,

    `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,width=${w},height=${h},top=${top},left=${left}`
  );
  if (focus === true && window.focus) popup.focus();
  return popup;
}

/**
 * Use this to add the debounve functionality to any functions.
 * 
 * Debounced functions will only be executed AFTER a given time has passed after
 * its last call. useful when a function can be called multiple times but should
 * process only once, in the end.
 * 
 * @param {Function} fn The functioun to be debounced
 * @param {Number} wait The time to wait for it to stop
 * @returns Function
 */
export function debounce (fn, wait = 300) {
  let to = null;

  return (...args) => {
    clearTimeout(to);
    to = setTimeout((_) => fn(...args), wait);
  };
}

/**
 * Checks the level of security of a given password.
 * Passwords with more variations will score better, including upper and lower
 * case letters, symbols and numbers.
 * 
 * @param {String} Password The password to be checked
 * @returns String One of "strong", "medium", "week" or "insecure"
 */
export function checkPasswordStrength (pwd = '') {

  const strongPassword = new RegExp(`(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})`);
  const mediumPassword = new RegExp(`((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))`);

  if (strongPassword.test(pwd)) {
    return 'strong';
  }

  if (mediumPassword.test(pwd)) {
    return 'medium';
  }

  if (pwd.length > 6) {
    return 'weak';
  }

  return 'insecure';
}

/**
 * Used to apply a throttle functionality to any function.
 * 
 * Returns a function, that, when invoked, will only be triggered at most once
 * during a given window of time. Normally, the throttled function will run
 * as much as it can, without ever going more than once per `wait` duration;
 * but if you'd like to disable the execution on the leading edge, pass
 * `{leading: false}`. To disable execution on the trailing edge, ditto.
 * 
 * @param {Function} func The function to be throttled
 * @param {Number} wait The time in mileseconds to be the interval
 * @param {Object} options Accepts some options (like the `leading` one)
 * @returns Function The throttled function
 */
export function throttle (func, wait, options) {
  var context, args, result;
  var timeout = null;
  var previous = 0;
  if (!options) options = {};
  var later = function () {
    previous = options.leading === false ? 0 : Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  };

  return function () {
    var now = Date.now();
    if (!previous && options.leading === false) previous = now;
    var remaining = wait - (now - previous);
    context = this; // eslint-disable-line
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }
    return result;
  };
}

/**
 * It should crop a given string to a specific size.
 * 
 * @param {String} str The text to be cropped
 * @param {Number} limit The limit in size for the text
 * @param {Boolean} useEllipsis If it should add the ellipses (default is true)
 * @returns String
 */
export function cropString(str, limit = 30, useEllipsis = true) {
  if (str.length <= limit) {
    return str;
  }
  return str.substring(0, limit) + (useEllipsis ? '…' : '');
}

/**
 * Adds a script to the page's header if it has not been imported yet.
 * 
 * @param {String} src The script source
 * @param {String} id An id for the script
 * @returns Promise
 */
export function createScript(src, id, module) {
  return new Promise((resolve, reject) => {
    if (!document.querySelector('script#' + id)) {
      const script = document.createElement('script');
      script.src = src;

      if (module) {
        script.type = 'module';
      }

      script.onload = function () {
        resolve();
      };
      script.onError = function (event) {
        reject(event);
      };
      document.head.appendChild(script);
    } else {
      resolve();
    }
  });
}

export function daysToMilliseconds (days) {
  return days * 24 * 60 * 60 * 1000;
}

/**
 * Returns the name of the month for a given date.
 * 
 * @param {Date} data The date object
 * @param {String} lang The language (default is pt-BR)
 * @param {Boolean} long If uses the long version of the month's name
 * @returns String
 */
export function getMonthName(date, lang = 'pt-BR', long = true) {
  return new Date(date)
    .toLocaleString(lang, { month: long ? 'long' : 'short' });
}

/**
 * Will download a PDF (or other format, if specified) file based on its content.
 * 
 * @param {Base64} pdfBase64 The base64 content for the PDF
 * @param {String} format The format of the content (default is "application/pdf")
 * @param {String} fileName The name of the file to be downloaded (default is "file.pdf")
 */
export const downloadPDF = (
  pdfBase64,
  format = "application/pdf",
  fileName = "file.pdf"
) => {
  const urlFile = `data:${format};base64,${pdfBase64}`;
  const link = document.createElement("a");

  link.href = urlFile;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Will try and download a file based on its url.
 * 
 * @param {String} urlFile The address of the file to be downloaded
 * @param {String} filename The name of the file
 */
export const downloadFile = (urlFile, filename) => {
  var link = document.createElement('a');
  link.href = urlFile;
  link.download = filename || 'download';
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Takes the file selected within an input of type file and
 * transforms it into a base64 string.
 * 
 * @param {FileObject} file The file comming from the input
 */
export function inputToBase64 (file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      resolve(reader.result);
    };
    reader.onerror = function (error) {
      reject(error);
    };
  });
}

/**
 * Returns the age based on a date.
 * 
 * @param {String|Date} dateString The date of birth
 * @returns Number
 */
export function getAge(dateString) {
  const today = new Date();
  const birthDate = dateString instanceof Date
    ? dateString
    : new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// Author https://github.com/manishsaraan/email-validator

// eslint-disable-next-line
const tester = /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
/**
 * A function to validate e-mails.
 * 
 * @param {string} email The email to be validated
 * @returns Boolean
 */
export function validateEmail(email) {
  if (!email) return false;

  const emailParts = email.split('@');

  if (emailParts.length !== 2) return false;

  const account = emailParts[0];
  const address = emailParts[1];

  if (account.length > 64) return false;

  else if (address.length > 255) return false;

  const domainParts = address.split('.');
  if (domainParts.some(function (part) {
    return part.length > 63;
  })) return false;

  if (!tester.test(email)) return false;

  return true;
}

/**
 * Will validate the CPF (document ID in Brasil)
 * 
 * @param {String} cpf The cpf
 * @returns Boolean
 */
export function isValidCPF(cpf) {
  if (typeof cpf !== "string") return false;
  cpf = cpf.replace(/[\s.-]*/igm, '');
  if (
    !cpf ||
    cpf.length != 11 ||
    cpf == "00000000000" ||
    cpf == "11111111111" ||
    cpf == "22222222222" ||
    cpf == "33333333333" ||
    cpf == "44444444444" ||
    cpf == "55555555555" ||
    cpf == "66666666666" ||
    cpf == "77777777777" ||
    cpf == "88888888888" ||
    cpf == "99999999999"
  ) {
    return false;
  }
  let soma = 0;
  let resto;
  for (let i = 1; i <= 9; i++) {
    soma = soma + parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if ((resto == 10) || (resto == 11)) resto = 0;
  if (resto != parseInt(cpf.substring(9, 10))) return false;
  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma = soma + parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;
  if ((resto == 10) || (resto == 11)) resto = 0;
  if (resto != parseInt(cpf.substring(10, 11))) return false;
  return true;
}

export function monthDiff(DataInicio) {
  const [currentYear, currentMonth] = new Date().toISOString().match(/\d+/g);
  const [startYear, startMonth] = DataInicio.match(/\d+/g); // "YYYY-MM-DD"
  const dateFrom = new Date(startYear, startMonth);
  const dateTo = new Date(currentYear, currentMonth);
  return dateTo.getMonth() -
    dateFrom.getMonth() +
    (
      12 * (dateTo.getFullYear() - dateFrom.getFullYear())
    );
}

/**
 * Will open the share browser feature for mobile and desktop.
 * 
 * @param {String} title The title to be shared
 * @param {String} text The text message shared
 * @param {String} share The url
 */
export const openShare = (title, text, share) => {
  if (navigator) {
    /* eslint-disable */
    require('share-api-polyfill');
    /* eslint-enable */
    navigator.share({
      title,
      text,
      url: share,
    }, {
      print: false,
      facebook: false,
      twitter: false,
      linkedin: false,
      pinterest: false,
      language: 'pt'
    });
  }
};

/**
 * Retrieves the value of a given variable from the URL.
 * 
 * @param {String} variable The variable name
 * @param {String} url The url, source
 * @returns Any
 */
export function getQueryVar(variable = null, url = '') {
  url = url ? (new URL(url)) : window?.location;
  var query = url.search?.substring(1);
  var vars = query.split('&');
  const params = {};
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=');
    if (variable && decodeURIComponent(pair[0]) == variable) {
      return decodeURIComponent(pair[1]);
    }
    if (!variable) {
      params[pair[0]] = pair[1];
    }
  }
  return variable ? null : params;
}

/**
 * Returns an object with key=value pairs for every url comming from the URL.
 * 
 * @param {String} url The url
 * @returns Object
 */
export function getQueryVars(url = '') {
  return getQueryVar(null, url);
}

/**
 * Used to add accessibility to allow elements to act like buttons or inputs
 * listening to the ENTER key.
 * 
 * @param {Function} cb The callback function
 * @returns Function
 */
export function handleKeyboardA11y(cb, options) {
  return function (event) {
    if (event.key === 'Enter') {
      return cb(event);
    }

    if (
      options?.space ||
      event.target.role === 'radio' || event.target.role === 'checkbox'
    ) {
      if (event.key === ' ') {
        return cb(event);
      }
    }
  };
}

/**
 * Deals with the Google Maps API to treat the returned address object
 * and normalize it.
 * 
 * @param {GMapsObject} gmapAddr The address object from GMaps
 * @returns Object
 */
export function parseGMapsAddress(gmapAddr) {
  const addr = {
    fullAddress: gmapAddr.formatted_address,
    geometry: gmapAddr.geometry
  };

  gmapAddr.address_components.forEach(comp => {
    if (comp.types.includes('postal_code')) {
      addr.postal_code = comp.short_name;
      addr.cep = comp.short_name; // for retrocompatibility
      return;
    }
    if (comp.types.includes('route')) {
      addr.street = comp.short_name;
      addr.address = comp.short_name; // for retrocompatibility
      return;
    }
    if (comp.types.includes('sublocality_level_1')) {
      addr.neighborhood = comp.short_name;
      return;
    }
    if (comp.types.includes('administrative_area_level_2')) {
      addr.city = comp.short_name;
      return;
    }
    if (comp.types.includes('administrative_area_level_1')) {
      addr.uf = comp.short_name;
      addr.state = comp.long_name;
      return;
    }
    if (comp.types.includes('country')) {
      addr.countryId = comp.short_name;
      addr.country = comp.long_name;
      return;
    }
  });

  return addr;
}

/**
 * This function will load the address information for a given postal code and
 * return an Object (or a list of objects) with matching addresses.
 * 
 * @param {String} postalCode The postal code itself
 * @param {String} country The country Code (default is "BR")
 * @returns [Object] The address objects
 */
export async function getAddressByPostalCode(postalCode, country = 'BR') {
  const res = await fetch(
    `/api/address/by-postal-code?postal_code=${postalCode}&country=${country}`
  );
  const data = await res.json();
  const addresses = [];
  if (data.results && data.results.length) {
    data.results.forEach(result => {
      addresses.push(parseGMapsAddress(result));
    });
  }

  return addresses;
}

/**
 * Will use the parsed Address object to retrieve geo coordinates.
 * 
 * @param {Object} address The address parsef Object
 * @returns [Coords] The found coords with coordLat and coordLng
 */
export async function getCoordsForAddress(address) {
  const addrStr = address.number
    + ', ' + (address.address || address.street)
    + ' - ' + address.city
    + ', ' + (address.uf || address.state);
  const res = await fetch(
    `/api/address/coords-by-address?address=${addrStr}`
  );
  const data = await res.json();
  const results = [];
  if (data.results && data.results.length) {
    const loc = data.results[0].geometry.location;
    results.push({
      coordLat: loc.lat,
      coordLng: loc.lng
    });
  }
  return results;
}

/**
 * Will try and find the block window containing the given element.
 * 
 * @param {DOMElement} domEl The element within the block window
 * @returns BlockWindow Object
 */
export function findWindowFromChildElement(domEl) {
  const SAFE_LOOP_LIMIT = 99;
  let i = 0;
  do {
    i++;
    if (domEl.dataset.windowId) {
      return domEl.dataset.windowId;
    }
    domEl = domEl.parentElement;

    if (i > SAFE_LOOP_LIMIT) return null;
  } while (!domEl.dataset.windowId || domEl !== window);
  return null;
}

/**
 * Will try to store the given data to the user's clipboard.
 * 
 * @param {Any} value The value to be coppied
 * @param {Boolean} blob If it should be written as a blob/base64
 * @returns Promise
 */
export function copyToClipboard(value, blob = false) {
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    if (blob) {
      return navigator.clipboard.write([value]);
    }
    return navigator.clipboard.writeText(value);
  }
  return Promise.reject('The Clipboard API is not available.');
}

export function getFocusableElement(container) {
  const focussableElements = `a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])`;
  return container?.querySelector(focussableElements) || null;
}

/**
 * Ensures a function will take at least a given time to finish.
 * Specially useful for loaders or easing user interactions
 * 
 * (the proccess will be over, but the result will only be available when the time ends)
 * 
 * @param {Function} fn The function to be triggered after the specified time
 * @param {Number} timeout The time to wait for
 * @returns Promise
 */
export async function waitAtLeast(fn, timeout = 1000) {
  const ps = await Promise.all([
    fn,
    new Promise(resolve => function () {
      setTimeout(resolve, timeout);
    })
  ]);
  return ps[0];
}

/**
 * Receives an array and will divide it into chunks, returnin an Array of
 * Arrays with the same size.
 * The last item may have less items.
 * 
 * @param {Array} arr The array to be chunked
 * @param {Number} size The size of each chunk
 * @returns Array
 */
export function arrayChunk(arr, size) {
  return Array.from(
    {
      length: Math.ceil(arr.length / size)
    },
    (_, i) => {
      return arr.slice(i * size, i * size + size);
    }
  );
}

export function parseAddressFromFields(fieldDefinitionAddr, valueAddr) {
  const validKeys = Object.keys(fieldDefinitionAddr);
  const parsedValue = {};
  validKeys.forEach(vk => {
    parsedValue[vk] = valueAddr[vk];
  });

  return parsedValue;
}

/* eslint-disable */
/**
 * Will disable any tag within a given string
 * 
 * This function adds a `​` UTF-8 `U+200B ZERO WIDTH SPACE` symbol to any
 * opening tag included in a string, disabling it as a tag.
 * 
 * @param {String} str The string to have it's tags disabled
 * @returns String
 */
export function disableTagsWithinStrings (str) {
  str = str.toString();
  return str.replace(/</g, '<​');
}
/* eslint-enable */

/**
 * Receives an array and reorders it.
 * 
 * @param {Array} arr The array with the table columns
 * @returns Array
 */
export const itemsToUpdate = (rows) => rows.reduce(
  (acc, row, index) => {
    if (row.order !== index + 1) {
      return [...acc, {
        ...row,
        order: index + 1
      }];
    }
    return acc;
  }, []
);

export function findUserPermissionsFor (data, what, id) {
  const permissions = [];
  const idName = what + '_id';
  data.forEach(tuple => {
    if (id && tuple[idName] === id) {
      permissions.push(tuple);
    }
  });

  return permissions;
}

export function parseUserPermissions (data) {
  if (!data) {
    return null;
  }

  const orgs = {};

  data.forEach(tuple => {
    const orgData = orgs[tuple.org_id] || {};
    if (!orgData.owner) {
      orgData.owner = tuple.org_owner;
      orgData.ownerEmail = tuple.org_owner_email;
      orgData.color = tuple.org_color;
      orgData.secondary_color = tuple.org_secondary_color;
      orgData.icon = tuple.org_icon;
      orgData.name = tuple.org_name;
      orgData.id = tuple.org_id;
      orgData.logo = tuple.org_logo;
      orgData.tier = tuple.org_tier;
      orgData.products = {};
      orgs[tuple.org_id] = orgData;
    }

    if (tuple.product_id) {
      const productData = orgData.products[tuple.product_id] || {};
      if (!productData.id) {
        productData.id = tuple.product_id;
        productData.name = tuple.product_name;
        productData.color = tuple.product_color;
        productData.description = tuple.product_description;
        productData.tenants = {};
        orgData.products[tuple.product_id] = productData;
      }

      if (tuple.tenant_id) {
        const tenantData = productData.tenants[tuple.tenant_id] || {};
        if (!tenantData.id) {
          tenantData.id = tuple.tenant_id;
          tenantData.name = tuple.tenant_name;
          productData.tenants[tuple.tenant_id] = tenantData;
        }
      }
    }
  });

  return orgs;
}

/**
 * Moves an item from one position to another within an Array
 * 
 * @param Array array The full list of items
 * @param Number oldIndex The Index to be moved
 * @param Number newIndex The targeted new index
 * @returns the resulting array
 */
export function arrayMove(arr, oldIndex, newIndex) {

  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }

  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
  return arr;
}

/**
 * Checks if a value is a number
 * 
 * @param {any} value The value to be verified
 * @returns Boolean
 */
export function isNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

export function isNumberObject(n) {
  return (Object.prototype.toString.apply(n) === '[object Number]');
}

export function isCustomNumber(n){
  return isNumber(n) || isNumberObject(n);
}

export function isFunction (value) {
  return typeof value === 'function';
}

/**
 * Generates a hash string
 * 
 * It can be generated based on a given string (as a key)
 * or not.
 * 
 * @param {String} string Optional string to be used as key
 * @param {String} salt Optional A salt effect applied to it (usually, aa private ENV variable)
 * @returns String SHA1 hash
 */
export function generateHash(string = null, salt = '') {
  const c = typeof crypto === 'function' ? crypto() : crypto;
  const shasum = c.createHash('sha1');

  if (string) {
    shasum.update(string + salt);
  }

  return shasum.digest('hex');
}

export async function smallHash (size = 10) {
  // import { nanoid } from 'nanoid';
  const nanoid = (await import('nanoid'))?.nanoid || (_=> _);
  return nanoid(size);
}

export function generateUUID () {
  const c = typeof crypto === 'function' ? crypto() : crypto;
  return c.randomUUID();
}

export function toMD5 (str, salt = '') {

  const c = typeof crypto === 'function' ? crypto() : crypto;

  return crypto
    .createHash('md5')
    .update(str + salt)
    .digest('hex');
}

export function timeBasedId () {
  return Date.now()
    .toString(36) +
    Math.random()
      .toString(36)
      .substring(2);
}

export function isNullish(value) {
  if (value === null || value === undefined || value === void (0)) {
    return true;
  }
  return false;
}

export function nullOrValue(value, nullValue = null) {
  if (isNullish(value)) {
    return nullValue;
  }

  return value;
}

export function arrayOrNull(value) {
  if (!Array.isArray(value) || value?.filter(i => i).length === 0) {
    return null;
  }

  return value;
}

/**
 * Removes HTML and/or Markdown tags from a given text
 * 
 * @param {String} text The text containing HTML and/or MarkDown tags
 * @param {Object} options Optional options. Pass `removeMarkdown: true` to also clear any md tag
 * 
 * @returns String
 */
export function stripTags(text, options) {
  text = text.toString();

  if (options.removeMarkdown) {
    text = removeMarkdown(text);
  }

  const doc = new DOMParser().parseFromString(text.toString(), 'text/html');
  return doc.body.textContent;
}

/**
 * Removes any markdown tags from a text, resulting in the raw text
 *
 * @param {String} md The text containing markdown tags
 * @param {Object} options An optional object containing special options for the command
 * @returns String
 * 
 * @ref from: https://github.com/stiang/remove-markdown/blob/main/index.js
 */
export function removeMarkdown (md, options) {

  /* eslint-disable no-prototype-builtins */
  options = options || {};
  options.listUnicodeChar = options.hasOwnProperty('listUnicodeChar')
    ? options.listUnicodeChar
    : false;
  options.stripListLeaders = options.hasOwnProperty('stripListLeaders')
    ? options.stripListLeaders
    : true;
  options.gfm = options.hasOwnProperty('gfm')
    ? options.gfm
    : true;
  options.useImgAltText = options.hasOwnProperty('useImgAltText')
    ? options.useImgAltText
    : true;
  options.abbr = options.hasOwnProperty('abbr')
    ? options.abbr
    : false;
  options.replaceLinksWithURL = options.hasOwnProperty('replaceLinksWithURL')
    ? options.replaceLinksWithURL
    : false;
  options.htmlTagsToSkip = options.hasOwnProperty('htmlTagsToSkip')
    ? options.htmlTagsToSkip
    : [];
  /* eslint-enable no-prototype-builtins */

  var output = md || '';

  // Remove horizontal rules
  // (stripListHeaders conflict with this rule, which is why it has been moved to the top)
  output = output.replace(/^(-\s*?|\*\s*?|_\s*?){3,}\s*/gm, '');

  try {
    if (options.stripListLeaders) {
      if (options.listUnicodeChar)
        output = output
          .replace(
            /^([\s\t]*)([*\-+]|\d+\.)\s+/gm,
            options.listUnicodeChar + ' $1'
          );
      else
        output = output.replace(/^([\s\t]*)([*\-+]|\d+\.)\s+/gm, '$1');
    }
    if (options.gfm) {
      output = output
        // Header
        .replace(/\n={2,}/g, '\n')
        // Fenced codeblocks
        .replace(/~{3}.*\n/g, '')
        // Strikethrough
        .replace(/~~/g, '')
        // Fenced codeblocks
        .replace(/`{3}.*\n/g, '');
    }
    if (options.abbr) {
      // Remove abbreviations
      output = output.replace(/\*\[.*\]:.*\n/, '');
    }
    output = output
      // Remove HTML tags
      .replace(/<[^>]*>/g, '');

    var htmlReplaceRegex = new RegExp('<[^>]*>', 'g');
    if (options.htmlTagsToSkip.length > 0) {
      // Using negative lookahead. Eg. (?!sup|sub) will not match 'sup' and 'sub' tags.
      var joinedHtmlTagsToSkip = '(?!' + options.htmlTagsToSkip.join("|") + ')';

      // Adding the lookahead literal with the default regex for html. Eg./<(?!sup|sub)[^>]*>/ig
      htmlReplaceRegex = new RegExp(
        '<' +
        joinedHtmlTagsToSkip +
        '[^>]*>',
        'ig'
      );
    }

    /* eslint-disable no-useless-escape */
    output = output
      // Remove HTML tags
      .replace(htmlReplaceRegex, '')
      // Remove setext-style headers
      .replace(/^[=\-]{2,}\s*$/g, '')
      // Remove footnotes?
      .replace(/\[\^.+?\](\: .*?$)?/g, '')
      .replace(/\s{0,2}\[.*?\]: .*?$/g, '')
      // Remove images
      .replace(/\!\[(.*?)\][\[\(].*?[\]\)]/g, options.useImgAltText ? '$1' : '')
      // Remove inline links
      .replace(/\[([^\]]*?)\][\[\(].*?[\]\)]/g, options.replaceLinksWithURL
        ? '$2'
        : '$1'
      )
      // Remove blockquotes
      .replace(/^(\n)?\s{0,3}>\s?/gm, '$1')
      // .replace(/(^|\n)\s{0,3}>\s?/g, '\n\n')
      // Remove reference-style links?
      .replace(/^\s{1,2}\[(.*?)\]: (\S+)( ".*?")?\s*$/g, '')
      // Remove atx-style headers
      .replace(/^(\n)?\s{0,}#{1,6}\s*( (.+))? +#+$|^(\n)?\s{0,}#{1,6}\s*( (.+))?$/gm, `$1$3$4$6`)
      // Remove * emphasis
      .replace(/([\*]+)(\S)(.*?\S)??\1/g, '$2$3')
      // Remove _ emphasis. Unlike *, _ emphasis gets rendered only if 
      //   1. Either there is a whitespace character before opening _ and after closing _.
      //   2. Or _ is at the start/end of the string.
      .replace(/(^|\W)([_]+)(\S)(.*?\S)??\2($|\W)/g, '$1$3$4$5')
      // Remove code blocks
      .replace(/(`{3,})(.*?)\1/gm, '$2')
      // Remove inline code
      .replace(/`(.+?)`/g, '$1')
      // // Replace two or more newlines with exactly two? Not entirely sure this belongs here...
      // .replace(/\n{2,}/g, '\n\n')
      // // Remove newlines in a paragraph
      // .replace(/(\S+)\n\s*(\S+)/g, '$1 $2')
      // Replace strike through
      .replace(/~(.*?)~/g, '$1');
    /* eslint-enable no-useless-escape */
  } catch (e) {
    console.error(e);
    return md;
  }
  return output;
}


export function formatsFromDGs (DGs) {
  const output = {};

  for (let DGName in DGs) {
    const DG = DGs[DGName];
    const id = DG.groupId;
    output[id] = output[id] || {};
    DG.DIs.forEach(DI => {
      output[id][DI.name] = DI;
    });
  }
  return output;
}


export function maxMinusMin (a, b) {
  const max = Math.max(a, b);
  const min = Math.min(a, b);
  return max - min;
}

/**
 * Returns a human readable name for a given ammount of bytes
 * 
 * Examples:
 *  1024 => 1kb
 *  1048000 => 1mb
 * 
 * @param {Number} numberOfBytes The number of bytes to be parsed
 * @param {Number} decimals How precise is it?
 * @returns String The formated string
 */
export function bytesToStringName (numberOfBytes, decimals) {
  return formatBytes(numberOfBytes, decimals);
}

/**
 * Returns a human readable name for a given ammount of bytes.
 * 
 * If the option `short` is passed as true, then `bytes` becomes `b`
 * 
 * Examples:
 *  1024 => 1kb
 *  1048000 => 1mb
 * 
 * @param {Number} numberOfBytes The number of bytes to be parsed
 * @param {Number} decimals How precise is it?
 * @param {Boolean} short preffers a short version
 * @returns String The formated string
 */
export function formatBytes (bytes, decimals = 2, short = false) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = [
    (short ? 'b' : 'bytes' ), 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'
  ];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Uses the extension of a file to produce a
 * mimetype string.
 * 
 * @param {String} ext The extension
 * @returns String The mimeType
 */
export function getTypeBasedOnExtension (ext) {
  ext = (ext || '').toString();

  if (ext.match(/jpe?g$/)) {
    return 'image/jpeg';
  }

  if (ext.match(/webp$/)) {
    return 'image/webp';
  }

  return 'image/png';
}

/**
 * You send the mimeType of a file and it returns the
 * extension of it, including the `.`.
 * 
 * @param {String} type The mimtype of the file
 * @returns String The extension
 */
export function getExtensionBasedOnType (type) {
  type = (type || '').toString();

  if (type.match(/jpe?g$/)) {
    return '.jpg';
  }

  if (type.match(/webp$/)) {
    return '.webp';
  }

  if (type.match(/png$/)) {
    return '.png';
  }

  if (type.match(/pdf$/)) {
    return '.pdf';
  }

  const parts = type.split('/');
  if (parts[0] === 'plain') {
    return '.txt';
  }

  if (parts[1]?.endsWith('binary')) {
    return parts[1].replace(/-binary$/, '');
  }

  if (parts[1]?.includes('vnd')) {
    // a vendor file
    return parts[1].split('-').pop();
  }

  return parts[1]
    ? '.' + parts[1]
    : '';
}

/**
 * Receives a DOM Canvas Element and returns a File Object
 * to be used the same way as if the user had selected a
 * file from their disk or pasted it from clipboard.
 * 
 * @param {CanvasElement} canvas 
 * @param {String} filename 
 * @returns FileObject
 */
export function canvasToFile (canvas, filename) {
  const dataUrl = canvas.toDataURL('image/jpeg');
  const bytes = dataUrl.split(',')[0].indexOf('base64') >= 0 ?
    atob(dataUrl.split(',')[1]) :
    (window).unescape(dataUrl.split(',')[1]);
  const mime = dataUrl.split(',')[0].split(':')[1].split(';')[0];
  const max = bytes.length;
  const ia = new Uint8Array(max);
  for (var i = 0; i < max; i++) {
    ia[i] = bytes.charCodeAt(i);
  }

  const newImageFileFromCanvas = new File(
    [ia],
    (filename || 'filename') + getExtensionBasedOnType(mime),
    { type: mime }
  );
  return newImageFileFromCanvas;
}

// export function getImageInfo (image, options = {}) {
//   return new Promise((resolve, reject) => {
//     const img = new Image();
//     img.crossOrigin = "anonymous";
//     img.onload = async () => {
// //       const c = document.createElement("canvas");
// //       const ctx = c.getContext("2d");
// //       const imgUrl = new URL(image);
// //       const tempType = getTypeBasedOnExtension(
// //         options.type ||
// //         imgUrl?.pathname?.match(/\.[a-z0-1]+$/)?.[0]
// //       );

//       const width = options.width || img.naturalWidth;
//       const height = options.height || img.naturalHeight;

// //       c.width = width;
// //       c.height = height;

// //       ctx.drawImage(
// //         img,
// //         0, 0,
// //         img.naturalWidth, img.naturalHeight,
// //         0, 0,
// //         width, height
// //       );
// // debugger;
// //       const base64 = c.toDataURL(
// //         tempType,
// //         options.quality || 1
// //       ); // .split(';base64,')[1]

// //       // const mimeType = (base64.match(/^data:([a-z/]+)/)?.[1]);
// //         // ?.replace(/\/jpeg$/, '/jpg');
// //       // const extension = mimeType.split('/')[1];
// //       const extension = tempType.split('/')[1]?.replace(/jpeg/, 'jpg');

// //       const blob = await base64ToBlob(base64);
//       // const blob = await new Promise(resolve => c.toBlob(
//       //   blob => {
//       //     resolve(blob);
//       //   },
//       //   tempType || "image/jpeg",
//       //   options.quality || 1 // quality
//       // ));

//       resolve({
//         width,
//         height,
//         size: blob.size,
//         mimeType: tempType,
//         extension,
//         canvas: c
//       });
//     };

//     img.onerror = (error) => {
//       reject(error);
//     };

//     if (image) {
//       img.src = image;
//     }
//   });
// }

const REGEXP_DECIMALS = /\.\d*(?:0|9){12}\d*$/;

/**
 * Normalize decimal number.
 * Check out {@link https://0.30000000000000004.com/}
 * @param {number} value - The value to normalize.
 * @param {number} [times=100000000000] - The times for normalizing.
 * @returns {number} Returns the normalized number.
 */
export function normalizeDecimalNumber(value, times = 100000000000) {
  return REGEXP_DECIMALS.test(value)
    ? (Math.round(value * times) / times)
    : value;
}


/**
 * Convert BASE64 to BLOB
 * @param base64Image Pass Base64 image data to convert into the BLOB
 */
export function base64ToBlob (base64Image) {
  // Split into two parts
  const parts = base64Image.split(';base64,');

  // Hold the content type
  const imageType = parts[0].split(':')[1];

  // Decode Base64 string
  const decodedData = window.atob(parts[1]);

  // Create UNIT8ARRAY of size same as row data length
  const uInt8Array = new Uint8Array(decodedData.length);

  // Insert all character code into uInt8Array
  for (let i = 0; i < decodedData.length; ++i) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }

  // Return BLOB image after conversion
  return new Blob([uInt8Array], { type: imageType });
}

// regras de tres
export function percent (value, relatedTo, total = 100) {
  return (total * value) / relatedTo;
}
// returns 50 cuz 60 is 50% of 120
// console.log(percent(60, 120));

// returns 100 cuz 120 is 100% of 120
// console.log(percent(100, 120, 120));

export function percentFrom (value, inRelationTo) {
  return percent(value, inRelationTo);
}

export function percentTo (value, inRelationTo) {
  return percent(100, inRelationTo, value);
}

percent.calc = function percentObj (value) {
  return {
    // percent.calc(50).percentOf(100); // 50
    // percent.calc(50).percentOf(60); // 30
    // percent.calc(50).percentOf(50); // 25
    percentOf (val2) {
      return (val2 * value) / 100;
    },
    // percent.calc(50).relatedTo(100);  // 50 (%)
    // percent.calc(50).relatedTo(50);   // 100 (%)
    // percent.calc(50).relatedTo(200);  // 25 (%)
    // percent.calc(60).relatedTo(120);  // 50 (%)
    // percent.calc(180).relatedTo(120); // 150 (%)
    relatedTo (val2) {
      return (100 * value) / val2;
    },
  };
};

/**
 * Format a given number of seconds into a string
 * 
 * Examples:
 * formatSeconds(356);
 * formatSeconds(356, {hours: false});
 * formatSeconds(356, {hours: 'maybe'}); // will add the hours, only if not 0
 * formatSeconds(356, {toHours: true});
 * formatSeconds(356, {toMinutes: true});
 * 
 * @param {number} seconds The seconds to be parsed
 * @param {Object} options An object with available options
 * @returns String The parsed string
 */
export function formatSeconds (seconds = 0, options = {}) {

  seconds = parseFloat(seconds);

  let result = '';
  let label = '';

  if (options.toHours) {
    result = (seconds / 60 / 60).toFixed(options.decimals || 2);
    if (options.label) {
      label = 'h';
    }
  } else if (options.toMinutes) {
    result = (seconds / 60).toFixed(options.decimals || 2);
    if (options.label) {
      label = 'm';
    }
  } else {
    let str = new Date(seconds * 1000).toISOString().slice(11, 19).split(':');
    if (
      (options.hours === false) ||
      (options.hours === 'maybe' && str[0] === '00')
    ) {
      str.shift();
    }
    result = str.join(':');
  }

  return result + label;
}


// used to get the aspect ratio
const gcd = (a, b) => {
  return b
    ? gcd(b, a % b)
    : a;
};

export const commonRatios = {
  "16:9": [1920, 1080],
  "5:4": [1280, 1024],
  "4:3": [1024, 768],
  "3:2": [1440, 960],
  "8K": [7680, 4320],
  "5K": [5120, 2880],
  "4K": [3840, 2160],
  Retina: [2048, 1536],
  iPhone6plus: [1920, 1080],
  iPhone6: [1334, 750],
  iPhone5: [1136, 640],
  iPad: [1024, 768],
  Twitter: [1024, 512],
  WebBanner: [728, 90],
  VGA: [640, 480],
  HVGA: [320, 480],
};

export const commonBreakPoints = {
  'mobile': [430, 680],
  'mobile-landscape': [680, 430],
  'tablet': [780, 980],
  'tablet-landscape': [980, 780],
  'desktop': [1024],
  'desktop-large': [1440],
  // '4k': [2560],
};

export const accessibilityFXOptions = [
  { name: 'protanopia' },
  { name: 'protanomaly' },
  { name: 'deuteranopia' },
  { name: 'deuteranomaly' },
  { name: 'tritanopia' },
  { name: 'tritanomaly' },
  { name: 'achromatopsia' },
  { name: 'achromatomaly' },
  {
    name: 'blurred-vision',
    label: 'Blurred vision'
  },
  // { // TODO: Add support for a screen reader viewer
  //   name: 'screen-reader',
  //   label: 'Screen Reader'
  // },
];

/**
 * Calculates the aspect ration of given measures
 * 
 * @param {Number} width 
 * @param {Number} height 
 * @returns String The aspect ratio like `16:9`
 */
export function aspectRatio (width, height) {
  const divisor = gcd(width, height);

  return `${width / divisor}:${height / divisor}`;
};

export const COLORS_WHITE_CONTRASTING = [
  "#FF5733",
  "#C70039",
  "#900C3F",
  "#581845",
  "#1A5276",
  "#1E8449",
  "#229954",
  "#D4AC0D",
  "#9A7D0A",
  "#7D3C98",
  "#512E5F",
  "#4A235A",
  "#6C3483",
  "#922B21",
  "#7B241C",
  "#A04000",
  "#6E2C00",
  "#2E4053",
  "#1B4F72",
  "#2874A6",
  "#1F618D",
  "#148F77",
  "#117A65",
  "#0E6655",
  "#196F3D",
  "#239B56",
  "#28B463",
  "#D68910",
  "#B9770E",
  "#A04000",
  "#7E5109",
  "#6E2C00"
];

export function pickARandomColor () {
  return COLORS_WHITE_CONTRASTING[
    Math.floor(Math.random() * COLORS_WHITE_CONTRASTING.length)
  ];
}

export function pickNextRandomColor () {
  const tmpColor = COLORS_WHITE_CONTRASTING.shift();
  COLORS_WHITE_CONTRASTING.push(tmpColor);
  return tmpColor;
}

/**
 * Returns the number of seconds for a given time
 * 
 * @param {number} val 
 * @param {string} kind One of: hours, days, weeks, months, years
 * @returns number
 */
export function expireTimeToSeconds (val, kind) {
  const kindMultiplier = {
    hours: 3600,
    days: 86400,
    weeks: 604800,
    months: 2592000,
    years: 31536000
  };

  return val * (kindMultiplier[kind] || 1);
}

/**
 * Creates an event with a target including name and value to be used
 * by other methods.
 * 
 * The eventData object may include a type, or "generic" will be used.
 * Anything passed to eventData.data will be used as target.data as well.
 * 
 * @param {String} name The name of the value to be used
 * @param {Any} value The value to be assossiated to that name
 * @param {Object} eventData An object with extra, optional data for the event
 * @returns Event Object
 */
export function createFakeEvent (name, value, eventData = {}) {
  const event = new Event(eventData.type || 'generic');

  const target = eventData?.target || {};
  target.name = name;
  target.value = value;

  if (eventData?.scope) {
    target.scope = eventData?.scope;
  }
  target.data = eventData?.data || {};

  Object.defineProperty(event, 'target', {writable: false, value: target});

  return event;
}

/**
 * This will execute a forEach call using async functions for each item.
 * 
 * Please notice that although the returned value will be ordered as the
 * processed items within the array, their execution and callback timing
 * may vary.
 * 
 * @param {Array} array The array to be iterated
 * @param {Function} callback The async function to be called for each item in the array
 * @returns a promise that resolves after all callbacks have finished
 */
export async function asyncForEach (array = [], callback) {
  const promises = [];
  array?.forEach((item, idx) => {
    promises.push(callback(item, idx));
  });
  return await Promise.all(promises);
}

/**
 * Validates a form, considering it may have other forms
 * as children. So, the validation runs recursively
 * 
 * @param {HTMLFormElement} form The form to be validated
 * @returns boolean
 */
export function validateForm (form, silently) {
  const forms = [
    form,
    ...Array.from(form?.querySelectorAll('form') || [])
  ];

  return !forms.find(f => {
    if (!f) {
      return true;
    }

    if (!f.checkValidity) {
      return false; // not an HTML form element
    }

    return silently
      ? !f.checkValidity()
      : !reportValidity(f);
  });
}

// {
//   name: {
//     type: 'string',
//     required: true,
//     group: 'test'
//   },
//   age: {
//     type: 'string',
//     required: true,
//     group: 'test'
//   },
//   id: {
//     type: 'string',
//     required: true,
//     group: 'test'
//   },
// }
export function reportValidity (form) {
  const checked = form.reportValidity();

  if (!checked) {
    const invalid = form.querySelector(`:invalid`);
    if (!invalid.offsetParent) {
      // this means the browser could NOT show the messag!
      // let's find a container that has it hidden
      // like an accordion
      const notVisibleContainerTrigger = form.querySelector(
        `.accordion:has(:invalid) .title`
      );

      if (notVisibleContainerTrigger) {
        // if we found a trigger, we force click it to make it visible
        notVisibleContainerTrigger.click();
        // and try again in another cycle
        setTimeout(_ => form.reportValidity(), 200);
        return false;
      }
    }
  }
  return checked;
}

/**
 * This function will try to execute a given code safely.
 * 
 * It actually creates a scoped, sandboxed iframe and executes the
 * code into it, so no variables or global scope is passed along.
 * 
 * In reallity...no eval is actually used.
 * 
 * @param {String} code The code to be executed
 * @returns Object with either data or error after the execution
 */
export function safeEval (code) {
  const iframeId = 'safe-eval-iframe-executor';
  let frame = document.getElementById(iframeId);

  if (!frame) {
    frame = document.createElement('iframe');
    frame.id = iframeId;
    // frame.sandbox = 'allow-forms allow-scripts';
    frame.sandbox = 'allow-same-origin';
    // frame.sandbox = '';
    frame.className = 'hidden-element';
    document.body.appendChild(frame);
  }

  try {
    const F = frame.contentWindow.Function;
    return {
      data: (new F(`
        const window = {};
        const top = {};
        const self = {};
        const parent = {};
        const document = {};
        const navigator = {};
        const globalThis = {};
        const opener = {};
        document.defaultView = null;
        window.parent = null;
        window.frameElement = null;

        return ${code}
      `)).apply({}),
      error: null
    };
  } catch (error) {
    console.error(`Failed executing the code in safeEval:`, error.message);
    return {
      data: null,
      error
    };
  }
}

/**
 * Receives a string and uses a separator to create a hierarchical list
 * 
 * For example:
 * getPartialsFor('/aaa/bbb/ccc/ddd/eee')
 * Receives: [
 *   "/aaa",
 *   "/aaa/bbb",
 *   "/aaa/bbb/ccc",
 *   "/aaa/bbb/ccc/ddd",
 *   "/aaa/bbb/ccc/ddd/eee"
 * ]
 * 
 * This is specially useful when dealing with breadcrumbs.
 * 
 * @param {String} str The full string
 * @param {String} separator The separator (default is /)
 * @returns [String]
 */
export function getCumulativePartialsList (str = '', separator = '/') {
  const result = [];
  let full = '';
  const parts = str.split(separator);
  for (let i = 0, l = parts.length; i < l; i++) {
    if (parts[i]) {
      full += separator + parts[i];
      result.push(full);
    }
  }

  if (str.startsWith('/')) {
    result.unshift('/');
  }

  return result;
}

/**
 * A wraper around localStorage turning it async
 */
export const asyncLocalStorage = {
  /**
   * An async getItem function.
   * 
   * @param {String} itemName A string
   * @param {Object} Options ({throw: boolean})
   */
  async getItem (itemName, options = {}) {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        const msg = `It is not possible to use ${options.session ? 'session' : 'local'}Storage in the back end`;
        console.warn(msg);

        if (options.throw) {
          return reject(msg);
        } else {
          resolve(null);
        }
      }

      let storage = 'localStorage';
      if (options.session) {
        storage = 'sessionStorage';
      }

      let gotValue = window[storage].getItem(itemName);

      if (gotValue && gotValue.match) {
        if (gotValue.match(/(^\{.+\}$)|(^\[.+\]$)/)) {
          // it is probably a json
          try {
            gotValue = JSON.parse(gotValue);
          } catch (error) {
            // could not parse, let's just use it as it it
          }
        }
      }

      return resolve(gotValue);
    });
  },

  /**
   * An async setItem function.
   * 
   * @param {String} itemName A string
   * @param {Any} Value The value(an object, array or native value)
   * @param {Object} Options ({throw: boolean})
   */
  async setItem (itemName, value, options = {}) {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        const msg = `It is not possible to use ${options.session ? 'session' : 'local'}Storage in the back end`;
        console.warn(msg);

        if (options.throw) {
          return reject(msg);
        }
      }

      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }

      let storage = 'localStorage';
      if (options.session) {
        storage = 'sessionStorage';
      }

      return resolve(window[storage].setItem(itemName, value));
    });
  }
};

/**
 * A wrapper around sessionStorage makint it async
 */
export const asyncSessionStorage = {
  /**
   * An async getItem function.
   * 
   * @param {String} itemName A string
   * @param {Object} Options ({throw: boolean})
   */
  async getItem (name, options = {}) {
    options.session = true;
    return asyncLocalStorage.getItem(name, options);
  },

  /**
   * An async setItem function.
   * 
   * @param {String} itemName A string
   * @param {Any} Value The value(an object, array or native value)
   * @param {Object} Options ({throw: boolean})
   */
  async setItem (name, value, options = {}) {
    options.session = true;
    return asyncLocalStorage.setItem(name, value, options);
  },
};

export function getExtensionsForType (type) {
  if (type === '*') {
    return undefined;
  }

  const videosExt = ["mov", "avi", "mp4", "webm", "wmv", "flx", "vp9"];
  const audiosExt = ["mp3", "flac", "wav", "m4a", "aac", "ogg"];

  if (type === 'image') {
    return ["png", "webp", "jpg", "jpeg", "gif", "svg"];
  }

  if (type === 'video') {
    return videosExt;
  }

  if (type === 'audio') {
    return audiosExt;
  }

  if (type === 'media' || type === 'images and videos') {
    return [...videosExt, ...audiosExt];
  }

  if (type === 'document') {
    return [
      "pdf", "xls", "xlsx", "csv", "doc", "docx", "txt",
      "md", "ppt", "pptx", "rtf", "odt", "html", "htm"
    ];
  }

  return [];
}

/**
 * Parses a CSV string into an array of arrays or object with headers and data
 * 
 * @param {string} csvString - The CSV string to parse
 * @param {Object} settings - Parser settings
 * @param {string} [settings.separator=','] - The separator character between values
 * @param {boolean} [settings.hasHeaders=false] - Whether the CSV has header row
 * @returns {Array|Object} Array of arrays if no headers, or object with headers and data arrays if hasHeaders=true
 */
export function parseCSV (csvString, settings = {}) {
  let separator = settings.separator || ',';
  const hasHeaders = settings.hasHeaders || false;
  const altSeparator = '\t';

  // Split into lines, filtering out empty ones
  const lines = csvString.toString()
    .split(/\r?\n/)
    .filter(line => line.trim());

  if (!lines.length) return [];

  if (
    lines[0].includes(altSeparator)
    // && !lines[0].includes(separator)
  ) {
    // possibly comming from a spreadsheet instead of a csv file
    separator = altSeparator;
  }

  const parseLine = line => {
    const values = [];
    let currentValue = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (insideQuotes && (line[i - 1] === '\\' || line[i + 1] === '"')) {
          // handle escaped quotes
          currentValue += '"';
          if (line[i + 1] === '"') {
            i++;
          }
        } else {
          // toggle quote mode
          insideQuotes = !insideQuotes;
        }
      } else if (char === separator && !insideQuotes) {
        // end of value
        values.push(currentValue);
        currentValue = '';
      } else {
        currentValue += char;
      }
    }

    // push the last value
    values.push(currentValue.trim());
    return values;
  };

  const result = lines.map(parseLine);

  // if has headers, remove first row from data but keep it accessible
  if (hasHeaders) {
    const headers = result.shift();
    return {
      headers,
      data: result
    };
  }

  return result;
}

export function isDate (val) {

  if (val instanceof Date) {
    return true;
  }

  if (isNumber(val)) {
    try {
      val = new Date(val);

      return val.toString() !== 'Invalid Date';
    } catch (error) {
      return false;
    }
  }

  return false;
}

export function formatDateForInput(date, type = 'date') {
  if (!(date instanceof Date) || isNaN(date)) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  switch (type) {
  case 'date':
    return `${year}-${month}-${day}`;
  case 'time':
    return `${hours}:${minutes}`;
  case 'datetime':
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
  case 'datetime-local':
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  default:
    return '';
  }
}

export function checkMatch (stringA = '', stringB = '', type, options = { ignoreCase: false, result: null }) {
  if (options.ignoreCase) {
    stringA = stringA.toLowerCase();
    stringB = stringB.toLowerCase();
  }

  let result = false;

  if (type === 'exact' || type === 'equals') {
    result = stringA === stringB ? (options.result || stringA) : false;
  }

  if (type === 'startswith') {
    result = stringA.startsWith(stringB) ? (options.result || stringA) : false;
  }

  if (type === 'endswith') {
    result = stringA.endsWith(stringB) ? (options.result || stringA) : false;
  }
  
  if (type === 'contains') {
    result = stringA.includes(stringB) ? (options.result || stringA) : false;
  }

  if (type === 'regex') {
    const rx = new RegExp(stringB, 'ig');
    if (stringA.match(rx)) {
      if (options.result) {
        result = stringA.replace(rx, options.result);
      } else {
        result = stringA;
      }
    }
  }

  if (type === 'wildcard') {
    const rx = new RegExp(stringB.replace(/\*/g, '(.+)').replace(/\*\.?\*/g, '(.*)'), 'ig');
    if (stringA.match(rx)) {
      if (options.result) {
        result = stringA.replace(rx, options.result);
      } else {
        result = stringA;
      }
    }
  }

  return result;
}
